/**
 * ToolExecutorService - Handles execution of SQL queries and external tools
 * Extracted from BaseLLM.generateTextResponse for better separation of concerns
 */

import { ensureQueryLimit } from '../utils';
import { logger } from '../../utils/logger';
import { userPromptErrorLogger } from '../../utils/user-prompt-error-logger';
import { StreamBuffer, streamDelay, withProgressHeartbeat } from '../stream-buffer';
import {
	formatQueryResultForLLM,
	formatToolResultForLLM,
	formatResultAsString
} from '../llm-result-truncator';
import {
	MAX_QUERY_ATTEMPTS,
	MAX_TOOL_ATTEMPTS,
	MAX_AGENT_QUERY_LIMIT,
	DEFAULT_MAX_ROWS_FOR_LLM,
	DEFAULT_MAX_CHARS_PER_FIELD,
	STREAM_PREVIEW_MAX_ROWS,
	STREAM_PREVIEW_MAX_CHARS,
	TOOL_TRACKING_MAX_ROWS,
	TOOL_TRACKING_MAX_CHARS,
	TOOL_TRACKING_SAMPLE_ROWS
} from '../constants';

/**
 * External tool definition
 */
export interface ExternalTool {
	id: string;
	name: string;
	description?: string;
	/** Tool type: "source" = routed through SourceAgent, "direct" = called directly by MainAgent */
	toolType?: 'source' | 'direct';
	/** Full untruncated schema for source agent (all columns visible) */
	fullSchema?: string;
	/** Schema size tier: small (≤50 tables), medium (51-200), large (201-500), very_large (500+) */
	schemaTier?: string;
	/** Schema search function for very_large tier — keyword search over entities */
	schemaSearchFn?: (keywords: string[]) => string;
	fn: (input: any) => Promise<any>;
	limit?: number;
	outputSchema?: any;
	executionType?: 'immediate' | 'deferred';
	userProvidedData?: any;
	params?: Record<string, any>;
}

/**
 * Executed tool tracking info
 */
export interface ExecutedToolInfo {
	id: string;
	name: string;
	params: any;
	result: {
		_totalRecords: number;
		_recordsShown: number;
		_metadata?: any;
		_sampleData: any[];
		/** Bounded summary over the FULL fetched result (complete structure). */
		_summary?: any;
		/** Up to MAIN_AGENT_COMPLETE_ROWS rows — the complete result when small. */
		_mainAgentRows?: any[];
	};
	outputSchema?: any;
	sourceSchema?: string;
	sourceType?: string;
}

/**
 * Configuration for ToolExecutorService
 */
export interface ToolExecutorServiceConfig {
	providerName: string;
	collections: any;
	streamBuffer: StreamBuffer;
}

/**
 * Result of tool handler execution
 */
export interface ToolHandlerResult {
	handler: (toolName: string, toolInput: any) => Promise<string>;
	getExecutedTools: () => ExecutedToolInfo[];
	isMaxAttemptsReached: () => boolean;
}

/**
 * ToolExecutorService handles SQL query and external tool execution
 */
export class ToolExecutorService {
	private config: ToolExecutorServiceConfig;
	private queryAttempts = new Map<string, number>();
	private toolAttempts = new Map<string, number>();
	private executedToolsList: ExecutedToolInfo[] = [];
	private maxAttemptsReached = false;

	constructor(config: ToolExecutorServiceConfig) {
		this.config = config;
	}

	/**
	 * Reset state for a new execution
	 */
	reset(): void {
		this.queryAttempts.clear();
		this.toolAttempts.clear();
		this.executedToolsList = [];
		this.maxAttemptsReached = false;
	}

	/**
	 * Get list of successfully executed tools
	 */
	getExecutedTools(): ExecutedToolInfo[] {
		return this.executedToolsList;
	}

	/**
	 * Check if max attempts were reached
	 */
	isMaxAttemptsReached(): boolean {
		return this.maxAttemptsReached;
	}

	/**
	 * Create a tool handler function for LLM.streamWithTools
	 * @param externalTools - List of available external tools
	 * @returns Tool handler function
	 */
	createToolHandler(externalTools?: ExternalTool[]): (toolName: string, toolInput: any) => Promise<string> {
		return async (toolName: string, toolInput: any): Promise<string> => {
			if (toolName === 'execute_query') {
				return this.executeQuery(toolInput);
			} else {
				return this.executeExternalTool(toolName, toolInput, externalTools);
			}
		};
	}

	/**
	 * Execute a SQL query with retry tracking and streaming feedback
	 */
	private async executeQuery(toolInput: any): Promise<string> {
		let sql = toolInput.sql;
		const params = toolInput.params || {};
		const reasoning = toolInput.reasoning;
		const { streamBuffer, collections, providerName } = this.config;

		// Ensure query has proper LIMIT clause
		sql = ensureQueryLimit(sql, MAX_AGENT_QUERY_LIMIT, MAX_AGENT_QUERY_LIMIT);

		// Track attempts for this query pattern (normalized)
		const queryKey = sql.toLowerCase().replace(/\s+/g, ' ').trim();
		const attempts = (this.queryAttempts.get(queryKey) || 0) + 1;
		this.queryAttempts.set(queryKey, attempts);

		if (Object.keys(params).length > 0) {
			logger.info(`[${providerName}] Query params: ${JSON.stringify(params)}`);
		}

		// Check if max attempts reached
		if (attempts > MAX_QUERY_ATTEMPTS) {
			const errorMsg = `Maximum query attempts (${MAX_QUERY_ATTEMPTS}) reached. Unable to generate a valid query for your question.`;
			logger.error(`[${providerName}] ${errorMsg}`);

			this.maxAttemptsReached = true;

			if (streamBuffer.hasCallback()) {
				streamBuffer.write(`\n\n❌ ${errorMsg}\n\nPlease try rephrasing your question or simplifying your request.\n\n`);
			}

			throw new Error(errorMsg);
		}

		try {
			// Flush any buffered LLM text before showing tool execution messages
			streamBuffer.flush();

			// Stream query generation message to frontend
			if (streamBuffer.hasCallback()) {
				const paramsDisplay = Object.keys(params).length > 0 ? `\n**Parameters:** ${JSON.stringify(params)}` : '';
				if (attempts === 1) {
					streamBuffer.write(`\n\n🔍 **Analyzing your question...**\n\n`);
					await streamDelay();
					if (reasoning) {
						streamBuffer.write(`💭 ${reasoning}\n\n`);
						await streamDelay();
					}
					streamBuffer.write(`📝 **Generated SQL Query:**\n\`\`\`sql\n${sql}\n\`\`\`${paramsDisplay}\n\n`);
					await streamDelay();
				} else {
					streamBuffer.write(`\n\n🔄 **Retrying with corrected query (attempt ${attempts}/${MAX_QUERY_ATTEMPTS})...**\n\n`);
					await streamDelay();
					if (reasoning) {
						streamBuffer.write(`💭 ${reasoning}\n\n`);
						await streamDelay();
					}
					streamBuffer.write(`📝 **Corrected SQL Query:**\n\`\`\`sql\n${sql}\n\`\`\`${paramsDisplay}\n\n`);
					await streamDelay();
				}
			}

			// Validate collections
			if (!collections?.['database']?.['execute']) {
				throw new Error('Database collection not registered. Please register database.execute collection to execute queries.');
			}

			// Build the query payload
			const queryPayload = Object.keys(params).length > 0
				? { sql: JSON.stringify({ sql, values: params }) }
				: { sql };

			// Execute query with progress heartbeat
			const result = await withProgressHeartbeat(
				() => collections['database']['execute'](queryPayload),
				'Executing database query',
				streamBuffer
			);

			// Extract data from result
			const data = (result as any)?.data || result;
			const rowCount = (result as any)?.count ?? (Array.isArray(data) ? data.length : 'N/A');


			// Stream success message and data preview
			if (streamBuffer.hasCallback()) {
				streamBuffer.write(`\n✅ **Query executed successfully!**\n\n`);
				await streamDelay();

				if (Array.isArray(data) && data.length > 0) {
					const firstRow = data[0];
					const columns = Object.keys(firstRow);

					if (data.length === 1 && columns.length === 1) {
						const value = firstRow[columns[0]];
						streamBuffer.write(`**Result:** ${value}\n\n`);
						await streamDelay();
					} else if (data.length > 0) {
						streamBuffer.write(`**Retrieved ${rowCount} rows**\n\n`);
						await streamDelay();

						const streamPreview = formatQueryResultForLLM(data, {
							maxRows: STREAM_PREVIEW_MAX_ROWS,
							maxCharsPerField: STREAM_PREVIEW_MAX_CHARS
						});

						streamBuffer.write(`<DataTable>${JSON.stringify(streamPreview.data)}</DataTable>\n\n`);
						if (streamPreview.truncationNote) {
							streamBuffer.write(`*${streamPreview.truncationNote}*\n\n`);
						}
						await streamDelay();
					}
				} else if (Array.isArray(data) && data.length === 0) {
					streamBuffer.write(`**No rows returned.**\n\n`);
					await streamDelay();
				}

				streamBuffer.write(`📊 **Analyzing results...**\n\n`);
			}

			// Format and truncate result for LLM consumption
			const formattedResult = formatQueryResultForLLM(data, {
				maxRows: DEFAULT_MAX_ROWS_FOR_LLM,
				maxCharsPerField: DEFAULT_MAX_CHARS_PER_FIELD
			});

			if (formattedResult.truncationNote) {
				logger.info(`[${providerName}] Truncation: ${formattedResult.truncationNote}`);
			}

			return formatResultAsString(formattedResult);
		} catch (error) {
			const errorMsg = error instanceof Error ? error.message : String(error);
			logger.error(`[${providerName}] Query execution failed (attempt ${attempts}/${MAX_QUERY_ATTEMPTS}): ${errorMsg}`);

			userPromptErrorLogger.logSqlError(sql, error instanceof Error ? error : new Error(errorMsg), Object.keys(params).length > 0 ? Object.values(params) : undefined);

			if (streamBuffer.hasCallback()) {
				streamBuffer.write(`❌ **Query execution failed:**\n\`\`\`\n${errorMsg}\n\`\`\`\n\n`);

				if (attempts < MAX_QUERY_ATTEMPTS) {
					streamBuffer.write(`🔧 **Generating corrected query...**\n\n`);
				}
			}

			throw new Error(`Query execution failed: ${errorMsg}`);
		}
	}

	/**
	 * Execute an external tool with retry tracking and streaming feedback
	 */
	private async executeExternalTool(
		toolName: string,
		toolInput: any,
		externalTools?: ExternalTool[]
	): Promise<string> {
		const { streamBuffer, providerName } = this.config;

		const externalTool = externalTools?.find(t => t.id === toolName);
		if (!externalTool) {
			throw new Error(`Unknown tool: ${toolName}`);
		}

		// Track attempts for this tool
		const attempts = (this.toolAttempts.get(toolName) || 0) + 1;
		this.toolAttempts.set(toolName, attempts);


		// Check if max attempts reached
		if (attempts > MAX_TOOL_ATTEMPTS) {
			const errorMsg = `Maximum attempts (${MAX_TOOL_ATTEMPTS}) reached for tool: ${externalTool.name}`;
			logger.error(`[${providerName}] ${errorMsg}`);

			if (streamBuffer.hasCallback()) {
				streamBuffer.write(`\n\n❌ ${errorMsg}\n\nPlease try rephrasing your request or contact support.\n\n`);
			}

			throw new Error(errorMsg);
		}

		try {
			streamBuffer.flush();

			if (streamBuffer.hasCallback()) {
				if (attempts === 1) {
					streamBuffer.write(`\n\n🔗 **Executing ${externalTool.name}...**\n\n`);
				} else {
					streamBuffer.write(`\n\n🔄 **Retrying ${externalTool.name} (attempt ${attempts}/${MAX_TOOL_ATTEMPTS})...**\n\n`);
				}
				await streamDelay();
			}

			// Cap the limit parameter for LLM-initiated tool calls
			// This ensures external tools called by LLM don't return more than MAX_AGENT_QUERY_LIMIT rows
			// UI direct calls bypass this and can use higher limits
			const cappedToolInput = { ...toolInput };
			if (cappedToolInput.limit !== undefined && cappedToolInput.limit > MAX_AGENT_QUERY_LIMIT) {
				logger.info(`[${providerName}] Capping external tool limit from ${cappedToolInput.limit} to ${MAX_AGENT_QUERY_LIMIT}`);
				cappedToolInput.limit = MAX_AGENT_QUERY_LIMIT;
			} else if (cappedToolInput.limit === undefined) {
				// If no limit specified by LLM, set the max limit to ensure bounded results
				cappedToolInput.limit = MAX_AGENT_QUERY_LIMIT;
			}

			// Execute the external tool function with progress heartbeat
			const result = await withProgressHeartbeat(
				() => externalTool.fn(cappedToolInput),
				`Running ${externalTool.name}`,
				streamBuffer
			);


			// Track successfully executed tool (for passing to component matching)
			if (!this.executedToolsList.find(t => t.id === externalTool.id)) {
				const formattedForTracking = formatToolResultForLLM(result, {
					toolName: externalTool.name,
					toolLimit: externalTool.limit,
					maxRows: TOOL_TRACKING_MAX_ROWS,
					maxCharsPerField: TOOL_TRACKING_MAX_CHARS
				});

				this.executedToolsList.push({
					id: externalTool.id,
					name: externalTool.name,
					params: cappedToolInput,
					result: {
						_totalRecords: formattedForTracking.summary.totalRecords,
						_recordsShown: formattedForTracking.summary.recordsShown,
						_metadata: formattedForTracking.metadata,
						_sampleData: formattedForTracking.data.slice(0, TOOL_TRACKING_SAMPLE_ROWS)
					},
					outputSchema: externalTool.outputSchema
				});
			}

			if (streamBuffer.hasCallback()) {
				streamBuffer.write(`✅ **${externalTool.name} completed successfully**\n\n`);
				await streamDelay();
			}

			// Format and truncate result for LLM consumption
			const formattedToolResult = formatToolResultForLLM(result, {
				toolName: externalTool.name,
				toolLimit: externalTool.limit,
				maxRows: DEFAULT_MAX_ROWS_FOR_LLM,
				maxCharsPerField: DEFAULT_MAX_CHARS_PER_FIELD
			});

			if (formattedToolResult.truncationNote) {
				logger.info(`[${providerName}] Truncation: ${formattedToolResult.truncationNote}`);
			}

			return formatResultAsString(formattedToolResult);
		} catch (error) {
			const errorMsg = error instanceof Error ? error.message : String(error);
			logger.error(`[${providerName}] External tool ${externalTool.name} failed (attempt ${attempts}/${MAX_TOOL_ATTEMPTS}): ${errorMsg}`);

			userPromptErrorLogger.logToolError(externalTool.name, toolInput, error instanceof Error ? error : new Error(errorMsg));

			if (streamBuffer.hasCallback()) {
				streamBuffer.write(`❌ **${externalTool.name} failed:**\n\`\`\`\n${errorMsg}\n\`\`\`\n\n`);

				if (attempts < MAX_TOOL_ATTEMPTS) {
					streamBuffer.write(`🔧 **Retrying with adjusted parameters...**\n\n`);
				}
			}

			throw new Error(`Tool execution failed: ${errorMsg}`);
		}
	}
}
